import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@store/authStore'
import { useSettingsStore } from '@features/settings/store'
import { hydrateSchemaOverrides } from '@features/scope/schema/overridesCache'
import { AppLayout } from '@components/layout/AppLayout'
import { LoginPage } from '@features/auth/LoginPage'
import { HomePage } from '@features/home'
import { Quote } from '@features/quote'
import { ScopePage } from '@features/scope'
import { SchemaViewerPage } from '@features/scope/SchemaViewerPage'
import { ROICalculator, PublicROIView } from '@features/roi-calculator'
import { Settings } from '@features/settings'
import { TargetAccountsPage } from '@features/target-accounts'
import { DashboardsHomePage, PresalesDashboardPage, PresalesDealDetailPage, ImplementationsDashboardPage, ClientProjectsPage } from '@features/dashboards'
import { Toaster } from '@components/ui/Toaster'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore()
  const location = useLocation()

  // Load the global schema overrides once the user is authenticated.
  // This populates the in-memory cache that powers PDF "Information" copy
  // and schema viewer labels for everyone in the team.
  useEffect(() => {
    if (isAuthenticated) void hydrateSchemaOverrides()
  }, [isAuthenticated])

  // Pull the team-wide scope-hours settings down from the server so every
  // user calculates auto-hours from the same source. Falls back silently to
  // DEFAULT_HOUR_SETTINGS if no row exists yet or the fetch fails.
  useEffect(() => {
    if (isAuthenticated) void useSettingsStore.getState().loadHourSettings()
  }, [isAuthenticated])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[var(--bg-primary)]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return <>{children}</>
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        {/* Public route - no auth required, customer-facing ROI view */}
        <Route path="/roi/share" element={<PublicROIView />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/home" replace />} />
          <Route path="home" element={<HomePage />} />
          <Route path="quote" element={<Quote />} />
          <Route path="scope" element={<ScopePage />} />
          <Route path="scope/schema" element={<SchemaViewerPage />} />
          <Route path="roi" element={<ROICalculator />} />
          <Route path="dashboard" element={<DashboardsHomePage />} />
          <Route path="dashboard/presales" element={<Navigate to="/dashboard/presales/performance" replace />} />
          <Route path="dashboard/presales/performance" element={<PresalesDashboardPage tab="performance" />} />
          <Route path="dashboard/presales/operations" element={<PresalesDashboardPage tab="operations" />} />
          <Route path="dashboard/presales/calendar" element={<PresalesDashboardPage tab="calendar" />} />
          <Route path="dashboard/presales/tools" element={<PresalesDashboardPage tab="tools" />} />
          <Route path="dashboard/presales/tools/:toolId" element={<PresalesDashboardPage tab="tools" />} />
          <Route path="dashboard/presales/deal/:dealId" element={<PresalesDealDetailPage />} />
          {/* Dynamic person route — must be AFTER the static tab routes so
              React Router ranks them higher by specificity */}
          <Route path="dashboard/presales/:personName" element={<PresalesDashboardPage tab="person" />} />
          <Route path="dashboard/implementations" element={<Navigate to="/dashboard/implementations/inflight" replace />} />
          <Route path="dashboard/implementations/sow" element={<ImplementationsDashboardPage tab="sow" />} />
          <Route path="dashboard/implementations/inflight" element={<ImplementationsDashboardPage tab="inflight" />} />
          <Route path="dashboard/implementations/sequence-diagrams" element={<ImplementationsDashboardPage tab="sequence-diagrams" />} />
          <Route path="dashboard/implementations/testing-ground" element={<ImplementationsDashboardPage tab="testing-ground" />} />
          <Route path="dashboard/implementations/platform-journey" element={<ImplementationsDashboardPage tab="platform-journey" />} />
          <Route path="dashboard/implementations/impl-checklist" element={<ClientProjectsPage />} />
          <Route path="target-accounts" element={<TargetAccountsPage />} />
          <Route path="settings" element={<Settings />} />
          <Route path="settings/:tab" element={<Settings />} />
        </Route>
      </Routes>
      <Toaster />
    </BrowserRouter>
  )
}

export default App
